export interface FrameCounters {
  calls: number;
  triangles: number;
  geometries: number;
  textures: number;
}

export interface FrameSnapshot {
  fps: number;
  frameMs: number;
  p95FrameMs: number;
  samples: number;
  counters: FrameCounters;
}

const emptyCounters = (): FrameCounters => ({ calls: 0, triangles: 0, geometries: 0, textures: 0 });
const median = (sorted: number[]): number => {
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

/** Rolling frame intervals, not CPU render duration or GPU execution time. */
export class FrameMetrics {
  private intervals: number[] = [];
  private previousMs: number | undefined;
  private counters = emptyCounters();

  record(nowMs: number, counters: FrameCounters): void {
    this.counters = { ...counters };
    if (!Number.isFinite(nowMs) || (typeof document !== 'undefined' && document.hidden)) {
      this.discardInterval();
      return;
    }
    const previous = this.previousMs;
    this.previousMs = nowMs;
    if (previous === undefined) return;
    const delta = nowMs - previous;
    if (delta <= 0 || delta > 250) return;
    this.intervals.push(delta);
    if (this.intervals.length > 300) this.intervals.shift();
  }

  /** Break timing across visibility changes even when no hidden frame is delivered. */
  discardInterval(): void {
    this.previousMs = undefined;
  }

  snapshot(): FrameSnapshot {
    const sorted = [...this.intervals].sort((a, b) => a - b);
    const samples = sorted.length;
    return {
      fps: samples ? median(sorted.map(ms => 1000 / ms).reverse()) : 0,
      frameMs: samples ? median(sorted) : 0,
      // Nearest-rank percentile: ceil(0.95 * N), indexed from one.
      p95FrameMs: samples ? sorted[Math.ceil(samples * 0.95) - 1] : 0,
      samples,
      counters: { ...this.counters },
    };
  }

  reset(): void {
    this.intervals = [];
    this.discardInterval();
    this.counters = emptyCounters();
  }
}

/** Structurally compatible with THREE.WebGLRenderer; no WebGL needed for tests. */
interface RendererWithInfo {
  info: {
    render: Pick<FrameCounters, 'calls' | 'triangles'>;
    memory: Pick<FrameCounters, 'geometries' | 'textures'>;
  };
}

export class PerformancePanel {
  readonly root = document.createElement('div');
  readonly metrics = new FrameMetrics();
  private lastPaintMs: number | undefined;
  private disposed = false;

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'F8' || event.repeat) return;
    event.preventDefault();
    this.root.hidden = !this.root.hidden;
  };

  private readonly onVisibilityChange = (): void => {
    this.metrics.discardInterval();
  };

  constructor(private readonly renderer: RendererWithInfo, private readonly extra: () => string[] = () => []) {
    this.root.className = 'performance-panel';
    this.root.hidden = true;
    Object.assign(this.root.style, {
      position: 'fixed', top: '8px', right: '8px', zIndex: '10000',
      pointerEvents: 'none', whiteSpace: 'pre', padding: '10px 12px',
      background: 'rgba(0, 0, 0, 0.8)', color: '#fff',
      font: '12px/1.5 monospace', borderRadius: '6px',
    });
    this.paint();
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  /** Call once after renderer.render() using the animation frame timestamp. */
  update(nowMs: number): void {
    if (this.disposed) return;
    const { render, memory } = this.renderer.info;
    this.metrics.record(nowMs, {
      calls: render.calls, triangles: render.triangles,
      geometries: memory.geometries, textures: memory.textures,
    });
    if (!Number.isFinite(nowMs) || document.hidden) return;
    if (this.lastPaintMs !== undefined && nowMs >= this.lastPaintMs && nowMs - this.lastPaintMs < 500) return;
    this.lastPaintMs = nowMs;
    this.paint();
  }

  private paint(): void {
    const stats = this.metrics.snapshot();
    const value = (n: number): string => stats.samples ? n.toFixed(1) : '—';
    this.root.textContent = [
      'Performance · F8',
      `FPS (中央値): ${value(stats.fps)}`,
      `Frame ms (中央値): ${value(stats.frameMs)}`,
      `Frame ms (p95): ${value(stats.p95FrameMs)}`,
      `Samples: ${stats.samples} / 300`,
      `Calls: ${stats.counters.calls}`,
      `Triangles: ${stats.counters.triangles}`,
      `Geometries: ${stats.counters.geometries}`,
      `Textures: ${stats.counters.textures}`,
      'GPU ms: 未計測',
      ...this.extra(),
    ].join('\n');
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    window.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.root.remove();
    this.metrics.reset();
  }
}
